/**
 * @fileoverview Componente Organismo para la visualización y gestión de Respuestas Rápidas (Fast Answers) en el módulo de chat de Mercately.
 * Incluye renderizado de contenido multimedia, integración con equipos, acciones de copiar/enviar y soporte para respuestas múltiples.
 * Cumple con Atomic Design (Organism) y optimizaciones de renderizado (React.memo, useCallback, useMemo).
 *
 * @module FastAnswerMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.1.0
 */

import React, {
  useState,
  useCallback,
  useMemo,
  memo
} from "react";
import { useTranslation } from 'react-i18next';

import ReadMoreMC from "./ReadMoreMC";
import FastAnswerIconMC from "./FastAnswerIconMC";
import MultipleFastAnswer from "./MultipleFastAnswersModalMC";
import { renderTeamName, renderTeamColor } from "./QuickAnswersServiceMC";
import TooltipMC from "../../components/TooltipMC";
import VideoMC from "../../components/VideoMC";
import AudioMC from "../../components/AudioMC";
import ImageMC from "../../components/ImageMC";
import FilePreviewMC from "./FilePreviewMC";

/**
 * @typedef {Object} FastAnswerAttributes
 * @description Estructura de los atributos de una respuesta rápida.
 * @property {string} title - Título de la respuesta rápida.
 * @property {string} answer - Contenido de la respuesta rápida.
 * @property {string} kind - Tipo de respuesta ('text', 'image', 'audio', 'video', etc.).
 * @property {string} [image_url] - URL del archivo multimedia asociado.
 * @property {string} [file_type] - Tipo de archivo multimedia ('image', 'audio', 'video', etc.).
 * @property {Object} [additional_fast_answers] - Respuestas adicionales asociadas.
 */

/**
 * @typedef {Object} FastAnswer
 * @description Objeto de respuesta rápida.
 * @property {string|number} id - Identificador único de la respuesta.
 * @property {FastAnswerAttributes} attributes - Atributos de la respuesta rápida.
 */

/**
 * @typedef {Object} FastAnswerMCProps
 * @description Props del componente FastAnswerMC.
 * @property {FastAnswer} answer - Objeto de respuesta rápida a renderizar.
 * @property {boolean} [displayTeam=false] - Si se debe mostrar información del equipo.
 * @property {function} onSelectAnswer - Callback para seleccionar/copiar/enviar la respuesta.
 */

/**
 * Componente Organismo para mostrar una Respuesta Rápida con soporte multimedia, acciones y equipos.
 *
 * @component FastAnswerMC
 * @category Component - Organism
 *
 * @description
 * **Características:**
 * - Renderiza título, contenido y acciones de una respuesta rápida.
 * - Soporta archivos multimedia (imagen, audio, video, otros).
 * - Permite copiar o enviar la respuesta al input del chat.
 * - Muestra información del equipo si displayTeam es true.
 * - Soporta respuestas múltiples mediante modal.
 * - Optimizado con React.memo, useCallback y useMemo.
 *
 * **Atomic Design:**
 * - **Nivel:** Organism
 * - **Composición:** Usa átomos (TooltipMC, ImageMC, AudioMC, VideoMC), moléculas (ReadMoreMC), organismos (MultipleFastAnswer)
 *
 * @param {FastAnswerMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento renderizado de la respuesta rápida
 *
 * @example
 * <FastAnswerMC
 *   answer={answer}
 *   displayTeam={true}
 *   onSelectAnswer={handleSelectAnswer}
 * />
 *
 * @see {@link FastAnswerIconMC} Icono de tipo de respuesta
 * @see {@link MultipleFastAnswer} Modal de respuestas múltiples
 * @see {@link ReadMoreMC} Visualización de texto expandible
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */

const FastAnswerMC = memo(({
  answer,
  displayTeam = false,
  onSelectAnswer
}) => {
  const { t } = useTranslation('FastAnswer');
  const [displayContent, setDisplayContent] = useState(false);
  const [showMultipleAnswers, setShowMultipleAnswers] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  /**
   * Indica si existen respuestas adicionales asociadas.
   * @type {boolean}
   */
  const hasMultipleAnswers = useMemo(
    () => answer?.attributes?.additional_fast_answers?.data?.length > 0,
    [answer?.attributes?.additional_fast_answers?.data?.length]
  );

  /**
   * Determina si se debe renderizar el contenido multimedia.
   * @type {boolean}
   */
  const shouldRenderMedia = useMemo(
    () => displayContent && answer?.attributes?.image_url,
    [displayContent, answer?.attributes?.image_url]
  );

  /**
   * Tipo de archivo multimedia asociado a la respuesta.
   * @type {string}
   */
  const fileType = useMemo(
    () => answer?.attributes?.file_type,
    [answer?.attributes?.file_type]
  );

  // Memoized event handlers
  /**
   * Alterna la visualización del contenido multimedia.
   * @function toggleDisplayContent
   * @memberof FastAnswerMC
   */
  const toggleDisplayContent = useCallback(() => {
    setDisplayContent((prev) => !prev);
  }, []);

  /**
   * Alterna el modal de respuestas múltiples y selecciona la respuesta.
   * @function toggleMultipleAnswersModal
   * @memberof FastAnswerMC
   * @param {React.MouseEvent} e - Evento de click
   * @param {FastAnswer} _answer - Respuesta seleccionada
   */
  const toggleMultipleAnswersModal = useCallback((e, _answer) => {
    e.stopPropagation();
    setShowMultipleAnswers((prev) => !prev);
    setSelectedAnswer(_answer);
  }, []);

  /**
   * Copia la respuesta rápida al input del chat.
   * @function copyQuickAnswerToInput
   * @memberof FastAnswerMC
   */
  const copyQuickAnswerToInput = useCallback(() => {
    onSelectAnswer?.(answer, 'copy');
  }, [onSelectAnswer, answer]);

  /**
   * Envía la respuesta rápida directamente al chat.
   * @function handleSendQuickAnswer
   * @memberof FastAnswerMC
   */
  const handleSendQuickAnswer = useCallback(() => {
    onSelectAnswer?.(answer, 'send');
  }, [onSelectAnswer, answer]);

  /**
   * Maneja el click para mostrar respuestas múltiples.
   * @function handleMultipleAnswersClick
   * @memberof FastAnswerMC
   * @param {React.MouseEvent} e - Evento de click
   */
  const handleMultipleAnswersClick = useCallback((e) => {
    toggleMultipleAnswersModal(e, answer);
  }, [toggleMultipleAnswersModal, answer]);

  // Memoized components for conditional rendering
  /**
   * Renderiza la información del equipo si displayTeam es true.
   * @type {JSX.Element|null}
   */
  const TeamInfoComponent = useMemo(() => {
    if (!displayTeam) return null;
    return (
      <>
        <div className="tw-flex tw-mt-1 tw-ml-3 tw-mr-3">
          {renderTeamColor(answer.attributes)}
        </div>
        <span className="tw-text-sm tw-text-gray-900 tw-font-semibold">
          {renderTeamName(answer.attributes, t)}
        </span>
      </>
    );
  }, [displayTeam, answer?.attributes, t]);

  /**
   * Renderiza el componente multimedia según el tipo de archivo.
   * @type {JSX.Element|null}
   */
  const MediaComponent = useMemo(() => {
    if (!shouldRenderMedia) return null;

    const mediaClassName = (() => {
      if (fileType === 'image') return "tw-max-w-full tw-h-auto tw-rounded-lg tw-border";
      if (fileType === 'video') return "tw-w-full tw-max-h-64 tw-rounded-lg";
      if (fileType === 'audio') return "tw-w-full";
      return "tw-w-full tw-h-64 tw-rounded-lg tw-border";
    })();
    const mediaProps = { className: mediaClassName };

    switch (fileType) {
      case 'image':
        return (
          <ImageMC
            data={{ contentMediaUrl: answer.attributes.image_url }}
            alt={answer.attributes.title}
            className={mediaProps.className}
          />
        );
      case 'audio':
        return (
          <AudioMC
            data={{ contentMediaUrl: answer.attributes.image_url }}
            className={mediaProps.className}
          />
        );
      case 'video':
        return (
          <VideoMC
            data={{ contentMediaUrl: answer.attributes.image_url }}
            className={mediaProps.className}
          />
        );
      default:
        return (
          <FilePreviewMC
            fileUrl={answer.attributes.image_url}
            fileName={answer.attributes.file_name}
          />
        );
    }
  }, [
    shouldRenderMedia,
    fileType,
    answer?.attributes?.image_url,
    answer?.attributes?.title,
    answer?.attributes?.file_name
  ]);

  return (
    <div key={answer.id} className="tw-wrapper tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-gray-200 tw-mb-3">
      <div className="tw-flex tw-flex-col">
        <div className="tw-flex tw-flex-row tw-justify-between">
          <div className="tw-flex tw-flex-row tw-items-center">
            <div className="tw-mr-3">
              <FastAnswerIconMC type={answer.attributes.kind} />
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-flex-1">
              <span className="tw-text-default tw-text-gray-900 tw-font-medium">
                {answer.attributes.title}
              </span>
              {TeamInfoComponent}
            </div>
          </div>
          <div className="tw-flex tw-justify-center">
            <TooltipMC text={t('copy')} position="top">
              <div
                className="tw-bg-gray-50 tw-rounded-lg tw-flex tw-justify-center tw-items-center tw-w-8 tw-h-8 tw-mr-2 tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors"
                onClick={copyQuickAnswerToInput}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && copyQuickAnswerToInput()}
              >
                <i className="mi-copy-outline tw-text-gray-400 hover:tw-text-gray-600"></i>
              </div>
            </TooltipMC>

            <TooltipMC text={t('send')} position="top">
              <div
                className="tw-bg-gray-50 tw-rounded-lg tw-flex tw-justify-center tw-items-center tw-w-8 tw-h-8 tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors"
                onClick={handleSendQuickAnswer}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleSendQuickAnswer()}
              >
                <i className="mi-paper-plane-outline tw-text-gray-400 hover:tw-text-gray-600"></i>
              </div>
            </TooltipMC>
          </div>
        </div>
        <div className="tw-w-full tw-mt-3">
          {hasMultipleAnswers && (
            <span
              className="tw-text-xs tw-text-blue-600 tw-cursor-pointer tw-mb-2 tw-block hover:tw-text-blue-800 tw-transition-colors"
              onClick={handleMultipleAnswersClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleMultipleAnswersClick(e)}
            >
              {t('multipleAnswers')}
            </span>
          )}
          <div className="tw-space-y-2">
            {answer.attributes.kind === 'text' ? (
              <ReadMoreMC>{answer.attributes.answer}</ReadMoreMC>
            ) : (
              <>
                <small className="tw-whitespace-pre-line tw-text-sm tw-text-gray-700 tw-block">
                  <span
                    onClick={toggleDisplayContent}
                    className="tw-text-blue-600 tw-cursor-pointer hover:tw-text-blue-800 tw-transition-colors"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && toggleDisplayContent()}
                  >
                    {displayContent ? t('hideContent') : t('showContent')}
                  </span>
                </small>
                {shouldRenderMedia && (
                  <div className="tw-mt-3">
                    {MediaComponent}
                  </div>
                )}
                {displayContent && (
                  <ReadMoreMC>{answer.attributes.answer}</ReadMoreMC>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {showMultipleAnswers && selectedAnswer && (
        <MultipleFastAnswer
          toggleMultipleAnswersModal={toggleMultipleAnswersModal}
          showMultipleAnswers={showMultipleAnswers}
          selectedAnswer={selectedAnswer}
        />
      )}
    </div>
  );
});

FastAnswerMC.displayName = 'FastAnswerMC';

export default FastAnswerMC;
